import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/context/ModalContext";
import { useDirty } from "@/context/DirtyContext";
import {
  createFeedback,
  getFeedback,
  getFeedbackPopup,
  getFeedbacks,
  deleteFeedback,
  updateFeedback,
  changeFeedbackState,
  bulkFeedbackUpdate,
  getFeedbackCategories,
} from "@/api/program-steps";
import { createCampaign } from "@/api/notification";
import { capitalizeFirstWord, safePostMessageToOpener } from "@/lib/utils";

const addReminderFrequencyToDate = (startTime: Date, reminderFrequency?: string) => {
  if (!reminderFrequency) {
    return startTime.toISOString();
  }

  const normalizedFrequency = reminderFrequency.trim().toLowerCase();
  const frequencyMatch = /^(\d+)\s*([smhdw])$/.exec(normalizedFrequency);

  if (!frequencyMatch) {
    return startTime.toISOString();
  }

  const [, rawValue, rawUnit] = frequencyMatch;
  const value = Number(rawValue);
  const endTime = new Date(startTime);

  switch (rawUnit) {
    case "s":
      endTime.setSeconds(endTime.getSeconds() + value);
      break;
    case "m":
      endTime.setMinutes(endTime.getMinutes() + value);
      break;
    case "h":
      endTime.setHours(endTime.getHours() + value);
      break;
    case "d":
      endTime.setDate(endTime.getDate() + value);
      break;
    case "w":
      endTime.setDate(endTime.getDate() + value * 7);
      break;
    default:
      return startTime.toISOString();
  }

  return endTime.toISOString();
};

type CreateFeedbackResponse = {
  data?: {
    id?: string;
    _id?: string;
  };
  id?: string;
  _id?: string;
};

/**
 * Interface for the feedback request body
 */
export interface CreateFeedbackRequestBody {
  [key: string]: unknown;
  category?: string;
  name: string;
  description: string;
  sub_category_flag?: number;
  // Only include if sub_category_flag is 1
  sub_category?: string[];
  icon: string;
  feedback_log_type: string;
  feed_back_log_entity?: string[];
  other_entities_log_flag?: number;
  other_log_entity?: string[];
  feedback_target_ids?: string[];
  solvable_flag: number;
  resolution_requirement?: string;
  notification_flag?: number;
  learning_video?: string;
  solve_feedback_media?: string;
  resolution_options?: string[];
  how_to_resolve?: string;
  external_triggers?: Array<{
    id?: string;
    trigger_type?: string;
    trigger?: string;
    trigger_configuration?: Record<string, unknown>;
  }>;
  facial_verification_flag?: number;
  feature_requirement?: string;
  biometric_badge?: string[];
  location_check_flag?: number;
  who_requires_location?: string;
  location_badge?: string[];
  loc_tolerance_radius?: number;
  // Only include if solvable_flag is 1
  solve_entity?: string[];
  primary_entity?: string;
  visibility_flag: number;
  user_triggers_flag: number;
  // Only include if user_triggers_flag is 1
  log_entity?: string[];
  feedback_meaning?: string;
  feedback_resolve_method?: string;
  // Only include if external_trigger_flag is 1
  external_trigger_flag?: number;
  external_trigger_type?: string;
  external_trigger_type_category?: string;
  external_trigger_due_status?: string;
  days_to_late?: number;
  late_entity?: string;
  late_solve_entity?: string[];
  days_to_very_late?: number;
  very_late_entity?: string;
  very_late_solve_entity?: string[];
  automated_notification_rules?: {
    primary_notification_channel?: string;
    enable_very_late_sms_backup?: boolean;
    sms_backup_message?: string;
    enable_supervisor_escalation?: boolean;
    escalate_after?: string;
    due_now?: {
      message_title?: string;
      message_body?: string;
      action_link?: string;
      cta_button_text?: string;
      reminder_frequency?: string;
    };
    late?: {
      message_title?: string;
      message_body?: string;
      action_link?: string;
      cta_button_text?: string;
      reminder_frequency?: string;
    };
    very_late?: {
      message_title?: string;
      message_body?: string;
      action_link?: string;
      cta_button_text?: string;
      reminder_frequency?: string;
    };
  };
  notification_rules?: Record<string, unknown>;
  user_id: string;
  username: string;
  _id?: string;
}

/**
 * POST /feedback/program/{program_id}
 * Create a feedback for a program
 */
export const useCreateFeedback = () => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();

  return useMutation({
    mutationFn: ({ programId, payload }: { programId: string; payload: CreateFeedbackRequestBody }) => createFeedback(programId, payload),
    onSuccess: (response: CreateFeedbackResponse, { programId, payload }) => {
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback", programId] });

      // Reset dirty state to allow easy navigation
      setIsDirty({ isDirt: false, confirmAction: () => {} });

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "FEEDBACK_CREATED",
        programId: programId,
      });

      // Create a notification campaign for each populated automated notification rule
      const rules = payload.automated_notification_rules;
      if (rules) {
        const createdFeedbackId = response?.data?.id ?? response?.data?._id ?? response?.id ?? response?._id ?? payload._id;
        const ruleEntries: Array<{ key: "due_now" | "late" | "very_late"; label: string }> = [
          { key: "due_now", label: "Due Now" },
          { key: "late", label: "Late" },
          { key: "very_late", label: "Very Late" },
        ];
        const campaignPayloads = ruleEntries
          .filter(({ key }) => !!rules[key]?.message_title)
          .map(({ key, label }) => {
            const rule = rules[key]!;
            const scheduleStartTime = new Date();
            const recipientRoleIdByKey = {
              due_now: payload.primary_entity,
              late: payload.late_entity,
              very_late: payload.very_late_entity,
            };
            const recipientRoleId = recipientRoleIdByKey[key];

            return createCampaign({
              feedback_id: createdFeedbackId,
              program_id: programId,
              list_id: "string",
              created_by: payload.user_id,
              recipient_role_id: recipientRoleId,
              audience_list: [],
              campaign_name: `${payload.name} - ${label}`,
              notification_type: rules.primary_notification_channel,
              message_title: rule.message_title,
              message_body: rule.message_body,
              cta_button_text: rule.cta_button_text,
              cta_url: rule.action_link,
              in_app_content: {},
              triggers: key,
              approval_status: "pending",
              notification_template: "feedback",
              schedule_start_time: scheduleStartTime.toISOString(),
              schedule_end_time: addReminderFrequencyToDate(scheduleStartTime, rule.reminder_frequency),
              reminder_frequency: rule.reminder_frequency,
              fallback_flag: key === "very_late" ? 1 : 0,
              status: "sent",
            });
          });
        Promise.all(campaignPayloads).then(() => {
          queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        });
      }

      showModal("done", {
        title: "Feedback Created",
        description: "The feedback was created successfully.",
        onConfirm: () => window.close(),
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error.message,
      });
    },
  });
};

/**
 * GET /feedback/program/{program_id}
 * Get feedbacks for a specific program
 */
export function useGetFeedbacks(programId: string) {
  return useQuery({
    queryKey: ["feedbacks", programId],
    queryFn: () => getFeedbacks(programId),
    enabled: !!programId,
  });
}

/**
 * GET /feedback/{program_id}
 * Get feedback for a specific program
 */
export function useGetFeedback(programId: string) {
  return useQuery({
    queryKey: ["feedback", programId],
    queryFn: () => getFeedback(programId),
    enabled: !!programId,
  });
}

/**
 * GET /feedback/{feedback_id}
 * Get a specific feedback
 */
export function useGetFeedbackPopup(feedbackId: string) {
  return useQuery({
    queryKey: ["feedback-popup", feedbackId],
    queryFn: () => getFeedbackPopup(feedbackId),
    enabled: !!feedbackId,
  });
}

/**
 * DELETE /feedback/{feedback_id}
 * Delete a feedback
 */
export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();
  return useMutation({
    mutationFn: ({ feedbackId, programId }: { feedbackId: string; programId: string }) => deleteFeedback(feedbackId),
    onSuccess: (_, { programId }) => {
      queryClient.invalidateQueries({ queryKey: ["feedback"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-popup"] });
      showModal("done", {
        title: "Deleted",
        description: "The feedback was deleted successfully.",
        onConfirm: hideModal,
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error.error || "There was an error deleting the feedback.",
      });
    },
  });
};

/**
 * PUT /feedback/program/{program_id}/{feedback_id}
 * Update a feedback
 */
export const useUpdateFeedback = () => {
  const queryClient = useQueryClient();
  const { showModal } = useModal();
  const { setIsDirty } = useDirty();

  return useMutation({
    mutationFn: ({ programId, feedbackId, payload }: { programId: string; feedbackId: string; payload: CreateFeedbackRequestBody }) =>
      updateFeedback(programId, feedbackId, payload),
    onSuccess: (_, { programId }) => {
      // Invalidate all feedback-related queries
      queryClient.invalidateQueries({ queryKey: ["feedback"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-popup"] });

      // Reset dirty state to allow easy navigation
      setIsDirty({ isDirt: false, confirmAction: () => {} });

      // Notify parent window to refresh its queries
      safePostMessageToOpener({
        type: "FEEDBACK_UPDATED",
        programId: programId,
      });

      showModal("done", {
        title: "Feedback Updated",
        description: "The feedback was updated successfully.",
        onConfirm: () => window.close(),
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Error",
        description: error?.response?.data?.error ?? "An error occurred while updating the feedback.",
      });
    },
  });
};

/**
 * PUT /feedback/{feedback_id}
 * Change feedback state (start/stop)
 */
export const useChangeFeedbackState = () => {
  const queryClient = useQueryClient();
  const { showModal, hideModal } = useModal();

  return useMutation({
    mutationFn: ({ feedbackId, action, user, programId }: { feedbackId: string; action: "start" | "stop"; user: string; programId: string }) =>
      changeFeedbackState(feedbackId, action, user),
    onSuccess: (_, { programId }) => {
      // Invalidate feedback queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["feedback"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });
      queryClient.invalidateQueries({ queryKey: ["feedbacks", programId] });
      queryClient.invalidateQueries({ queryKey: ["feedback", programId] });
      queryClient.invalidateQueries({ queryKey: ["program-timeline", programId] });

      showModal("done", {
        title: "Feedback State Updated",
        description: "The feedback state was updated successfully.",
        onConfirm: () => {
          hideModal();
        },
      });
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Feedback State Update Failed",
        description: error?.error ?? error?.response?.data?.error ?? "An error occurred while updating the feedback state.",
      });
    },
  });
};

export const useBulkFeedbackUpdate = (setSelectedRows: (newSelectedRows: Set<string>) => void) => {
  const { showModal, hideModal } = useModal();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { action: string; feedback_ids: string[] }) => bulkFeedbackUpdate(payload),
    onSuccess: data => {
      const status = data?.status;
      // Invalidate feedback queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ["feedbacks"] });

      // Show modal if there is a validation error
      if (!status) {
        const errorMessage = data?.data?.errors[0]?.message || "Bulk update failed due to validation errors.";
        showModal("error-message", {
          title: capitalizeFirstWord(data?.message),
          description: errorMessage,
        });
      } else {
        showModal("done", {
          title: "Bulk Feedback Update Successful",
          description: "The selected feedback items were updated successfully.",
          onConfirm: () => {
            hideModal();
            setSelectedRows(new Set());
          },
        });
      }
    },
    onError: (error: any) => {
      showModal("error-message", {
        title: "Bulk Feedback Update Failed",
        description: capitalizeFirstWord(error?.message) || "Unable to update the feedback data. Please try again later.",
      });
    },
  });
};

export const useGetFeedbackCategories = () => {
  return useQuery({
    queryKey: ["feedback-categories"],
    queryFn: () => getFeedbackCategories(),
  });
};
